import {Sync} from '@client/sync'
import {DropdownButton} from '@components/common/dropdown-list/dropdown-button'
import {getMultiSelectText} from '@components/common/dropdown-list/dropdown-list'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {TriCheckbox} from '@components/common/tri-checkbox/tri-checkbox'
import {UsersTable} from '@components/common/users-table/users-table'
import {UsersTableUtil} from '@components/common/users-table/users-table-util'
import {Remove} from '@components/statistics/stats-reports/common'
import {useBoolean} from '@containers/hooks'
import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {isDataObjectId, ReportUserFilter} from 'yougile-model'
import {ArrUtil} from 'yougile-util'
import AvatarIcon from './avatar.svg'

interface OuterProps {
  filters: ReportUserFilter[]
  dontOpen?: boolean
  withInputDecryption?: boolean
  showRemoveButton?: boolean
  className?: string
  onOpenChange?: (isOpen: boolean) => void
  showUnderHeadElement?: boolean
  placeholder?: string
}

interface Props extends OuterProps {
  update: (filters: ReportUserFilter[]) => void
}

// eslint-disable-next-line max-lines-per-function
export const UserFilter = (props: Props) => {
  const {
    filters = [],
    dontOpen = false,
    withInputDecryption = false,
    showRemoveButton = true,
    className,
    onOpenChange,
    showUnderHeadElement = true,
    placeholder
  } = props
  const openState = useBoolean(!dontOpen && !props.filters)
  useEffect(() => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(openState.value)
    }
  }, [openState.value])
  const box = Sync.getCurrentSandbox()
  const allUsers = box
    .getAllUsers(false)
    .map(x => UsersTableUtil.convertUser(box, x))
  const notSet = !!filters.find(f => f.user === 'not-set')
  const selectedUserIds = filters.map(x => x.user).filter(isDataObjectId)
  const allUserOptions = allUsers.map(x => ({
    label: x.userName,
    value: x.userId
  }))

  const toggleUsers = (users: ReportUserFilter['user'][]) => {
    props.update(
      ArrUtil.xorBy(
        filters,
        users.map(user => ({user})),
        x => x.user
      )
    )
  }

  return (
    <div className="flex justify-between">
      <DropdownButton
        className={className ?? 'w-208'}
        content={
          <DropdownContent className="w-304 h-304" header={Lang.msg.coworkers}>
            <UsersTable
              className="h-full"
              underHeadElement={
                showUnderHeadElement && (
                  <div
                    className="flex items-center h-32 px-4 cursor-pointer"
                    onClick={() => toggleUsers(['not-set'])}
                  >
                    <div className="w-24 px-4">
                      <div className="flex items-center">
                        <TriCheckbox className="ml-4" value={notSet} />
                      </div>
                    </div>
                    <AvatarIcon className="mx-4" />
                    {placeholder ?? Lang.msg.statsUserNotSet}
                  </div>
                )
              }
              users={allUsers}
              useCheckbox={true}
              selected={selectedUserIds}
              onToggled={toggleUsers}
            />
          </DropdownContent>
        }
        open={openState.value}
        onOpenChange={openState.setValue}
      >
        {notSet && `${placeholder ?? Lang.msg.statsNotSet}, `}
        {getMultiSelectText({
          value: selectedUserIds,
          options: allUserOptions,
          placeholder
        })}
      </DropdownButton>
      {withInputDecryption && <UserFilterLabels {...props} />}
      {showRemoveButton && <Remove update={props.update} />}
    </div>
  )
}

export const UserFilterValue = (props: OuterProps) => {
  const {filters = [], placeholder = Lang.msg.statsTimeline.allUsers} = props
  const box = Sync.getCurrentSandbox()
  const allUsers = box
    .getAllUsers(false)
    .map(x => UsersTableUtil.convertUser(box, x))
  const notSet = !!filters.find(f => f.user === 'not-set')
  const selectedUserIds = filters.map(x => x.user).filter(isDataObjectId)
  const allUserOptions = allUsers.map(x => ({
    label: x.userName,
    value: x.userId
  }))

  return (
    <div className="truncate max-w-208">
      {notSet && `${placeholder ?? Lang.msg.statsNotSet}, `}
      {getMultiSelectText({
        value: selectedUserIds,
        options: allUserOptions,
        placeholder
      })}
    </div>
  )
}

const TwLabels = tw.div`
  flex-1
  w-8
  ml-16
  truncate
  text-third-old
  text-12
  self-center
`

const UserFilterLabels = (props: OuterProps) => {
  const {filters = []} = props
  const box = Sync.getCurrentSandbox()
  const allUsers = box
    .getAllUsers(false)
    .map(x => UsersTableUtil.convertUser(box, x))
  const selectedUserIds = filters.map(x => x.user).filter(isDataObjectId)
  const allUserOptions = allUsers.map(x => ({
    label: x.userName,
    value: x.userId
  }))

  const labels = allUserOptions
    .filter(x => selectedUserIds.includes(x.value))
    .map(x => x.label)
    .join(', ')

  return (
    selectedUserIds.length !== allUserOptions.length && (
      <TwLabels title={labels}>{labels}</TwLabels>
    )
  )
}
